import {
  Anchor,
  Button,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCurrentUser } from "../../hooks/useAuth.js";
import {
  useLeadsOverview,
  useMyLeadSegments,
  useZielrunde,
  useZielrundeFreigabe,
} from "../../hooks/useKpi.js";
import { LeadDetail } from "../../components/kpi/LeadsPanel.js";
import { freigabeKnopfState } from "../../lib/freigabeKnopf.js";

/**
 * „Mein Portfolio" — die reine Ich-Sicht des Service Leads: eigene
 * Kennzahlen (Vorjahr/Ziel/Ist/DB%/Prognose), eigene Beauftragungen mit
 * Inline-Pflege und Drill-down. Bewusst OHNE Lead-Überblick und ohne
 * Lead-Auswahl — der Vergleich mit anderen Service Leads wohnt im
 * Bereichs-Cockpit (Leads-Tab). Segment-Auswahl nur aus den Segmenten,
 * in denen die eigene Person Lead mindestens einer Beauftragung ist.
 */

const fmtEur = (v: number | null | undefined) =>
  v == null ? "–" : `${Math.round(v).toLocaleString("de-DE")} €`;
const fmtPct = (v: number | null | undefined) =>
  v == null ? "–" : `${v.toFixed(1)} %`;

export function KpiPortfolioPage() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const { data: mySegments, isLoading } = useMyLeadSegments();
  const currentYear = new Date().getFullYear();
  const [segmentId, setSegmentId] = useState<string | null>(null);
  const [year, setYear] = useState<number>(currentYear);

  // Vorbelegung: eigenes Segment (BL/Home-Bereich), sofern ich dort Lead
  // bin — sonst das erste eigene Lead-Segment.
  useEffect(() => {
    if (segmentId || !mySegments || mySegments.length === 0) return;
    const preferred =
      mySegments.find((s) => s.segmentId === user?.defaultSegmentId) ??
      mySegments[0]!;
    setSegmentId(preferred.segmentId);
  }, [mySegments, segmentId, user?.defaultSegmentId]);

  // Eigene Überblicks-Kennzahlen aus dem Leads-Overview des Segments ziehen.
  const { data: overview } = useLeadsOverview(segmentId, year);
  const me = overview?.find((l) => l.employeeId === user?.employeeId) ?? null;

  // Freigabe-Knopf: „meine Ziele sind fertig gepflegt". Ob er erscheint und
  // was er sagt, entscheidet eine reine Funktion (`freigabeKnopfState`) —
  // eingefroren heißt weg, nicht nur gesperrt, und er betrifft ausschließlich
  // die eigene Zeile (s. `apps/web/src/lib/freigabeKnopf.ts`).
  const { data: zielrunde } = useZielrunde(segmentId, year);
  const freigabe = useZielrundeFreigabe();
  const knopf = useMemo(
    () => freigabeKnopfState(zielrunde?.status, zielrunde?.leads, user?.employeeId),
    [zielrunde, user?.employeeId],
  );

  if (isLoading) {
    return <Skeleton height={320} />;
  }

  if (!mySegments || mySegments.length === 0) {
    return (
      <Stack gap="md">
        <Title order={2}>{t("pages.kpi.portfolio.title")}</Title>
        <Paper p="xl" withBorder>
          <Stack align="center" gap="sm">
            <Text fw={600}>{t("pages.kpi.portfolio.noLeadTitle")}</Text>
            <Text c="dimmed" size="sm" ta="center" maw={480}>
              {t("pages.kpi.portfolio.noLeadBody")}
            </Text>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Title order={2}>{t("pages.kpi.portfolio.title")}</Title>
          <Text c="dimmed" size="sm">
            {t("pages.kpi.portfolio.subtitle")}{" "}
            <Anchor component={Link} to="/kpi/bereich" size="sm">
              {t("pages.kpi.portfolio.compareLink")}
            </Anchor>
          </Text>
        </Stack>
        <Group gap="sm">
          <Select
            value={segmentId}
            data={mySegments.map((s) => ({
              value: s.segmentId,
              label: s.name,
            }))}
            onChange={setSegmentId}
            style={{ minWidth: 220 }}
            disabled={mySegments.length === 1}
          />
          <Select
            value={String(year)}
            data={[currentYear - 1, currentYear, currentYear + 1].map(String)}
            onChange={(v) => v && setYear(Number(v))}
            style={{ width: 100 }}
          />
        </Group>
      </Group>

      {/* Eigene Kennzahlen — dieselben Spalten wie der Lead-Überblick im
          Bereichs-Cockpit, hier als Karten nur für mich. */}
      {me && (
        <SimpleGrid cols={{ base: 2, md: 6 }}>
          <PortfolioStat
            label={t("pages.kpi.leads.colPrevYear")}
            value={fmtEur(me.prevYearRevenueEur)}
          />
          <PortfolioStat
            label={t("pages.kpi.leads.colTarget")}
            value={fmtEur(me.targetRevenueEur)}
          />
          <PortfolioStat
            label={t("pages.kpi.leads.colYtd")}
            value={fmtEur(me.actualYtdEur)}
          />
          <PortfolioStat
            label={t("pages.kpi.leads.colMargin")}
            value={fmtPct(me.marginCumPct)}
          />
          <PortfolioStat
            label={t("pages.kpi.leads.colForecast")}
            value={fmtEur(me.forecastEur)}
          />
          <PortfolioStat
            label={t("pages.kpi.leads.colForecastGrowth")}
            value={fmtPct(me.forecastGrowthPct)}
            color={
              me.forecastGrowthPct == null
                ? undefined
                : me.forecastGrowthPct >= 0
                  ? "green"
                  : "red"
            }
          />
        </SimpleGrid>
      )}

      {segmentId && user?.employeeId && (
        <>
          {knopf && (
            <Group justify="flex-end">
              <Button
                variant={knopf.freigegeben ? "light" : "filled"}
                loading={freigabe.isPending}
                onClick={() =>
                  freigabe.mutate({
                    segmentId,
                    year,
                    freigegeben: !knopf.freigegeben,
                  })
                }
              >
                {t(knopf.labelKey)}
              </Button>
            </Group>
          )}
          <LeadDetail
            segmentId={segmentId}
            year={year}
            leadId={user.employeeId}
          />
        </>
      )}
    </Stack>
  );
}

function PortfolioStat({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <Paper p="md" withBorder>
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text size="xl" fw={700} c={color}>
        {value}
      </Text>
    </Paper>
  );
}
